// A record's ticket in the organisation's own ticketing system (backend lib/ticketing.ts,
// docs/TICKETING.md): "Raise ticket" when it has none, otherwise the ticket's reference and status
// beside the platform's own. The ticket's status is shown, never taken as the platform's: a closed
// ticket completes an action at most and verification stays on the platform.

import { useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink, Ticket } from "lucide-react";
import { api, apiErrorMessage } from "../api/client";
import { btnSecondary, type as text } from "../theme";
import { StatusTag, type StatusTone } from "./Drawing";

export type TicketSubjectKind = "corrective_action" | "vulnerability" | "remediation_step";
export type TicketState = "open" | "in_progress" | "done" | "cancelled" | "unknown";

export interface TicketLinkRow {
  id: string;
  subjectRef: string;
  externalKey: string;
  externalUrl: string;
  ticketStatus: string | null;
  ticketState: TicketState;
  reopenedAt: string | null;
  completedActionAt: string | null;
  lastReadAt: string | null;
  lastReadError: string | null;
  autoPushed: boolean;
  providerLabel: string;
}

interface LinksResponse { connected: boolean; providerLabel: string | null; links: TicketLinkRow[] }

const STATE_LABELS: Record<TicketState, string> = { open: "Open", in_progress: "In progress", done: "Done", cancelled: "Cancelled", unknown: "Status not recognised" };
const STATE_TONE: Record<TicketState, StatusTone> = { open: "none", in_progress: "partial", done: "met", cancelled: "none", unknown: "none" };

/**
 * The tickets of one register's records. Quiet when the organisation's tier does not include
 * integrations or nothing is connected: `available` is false and nothing is shown.
 */
export function useTicketLinks(kind: TicketSubjectKind, refs?: readonly string[]) {
  const [data, setData] = useState<LinksResponse | null>(null);
  const key = refs ? refs.join(",") : "";
  const load = useCallback(() => {
    api.get<LinksResponse>("/ticketing/links", { kind, ...(key ? { refs: key } : {}) })
      .then(setData)
      .catch(() => setData(null));
  }, [kind, key]);
  useEffect(() => { load(); }, [load]);
  const byRef = useMemo(() => new Map((data?.links ?? []).map((l) => [l.subjectRef, l])), [data]);
  return {
    available: !!data,
    connected: !!data?.connected,
    providerLabel: data?.providerLabel ?? null,
    linkFor: (ref: string) => byRef.get(ref) ?? null,
    reload: load,
    remember: (l: TicketLinkRow) => setData((d) => (d ? { ...d, links: [...d.links.filter((x) => x.subjectRef !== l.subjectRef), l] } : d)),
  };
}

export type TicketLinks = ReturnType<typeof useTicketLinks>;

/** The ticket chip: its reference (opening the vendor's page) and its status as last read. */
export function TicketChip({ link, compact }: { link: TicketLinkRow; compact?: boolean }) {
  const read = link.lastReadAt ? `Last read ${new Date(link.lastReadAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}` : "Not read yet";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <a href={link.externalUrl} target="_blank" rel="noopener noreferrer" title={`Open in ${link.providerLabel}`}
        onClick={(e) => e.stopPropagation()}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, ...text.ref, fontWeight: 600, color: "var(--c-accentText)", textDecoration: "none" }}>
        <Ticket size={12} aria-hidden /> {link.externalKey} <ExternalLink size={11} aria-hidden />
      </a>
      <StatusTag tone={STATE_TONE[link.ticketState]} title={`${link.providerLabel} status: ${link.ticketStatus ?? STATE_LABELS[link.ticketState]}. ${read}.`}>
        {compact ? (link.ticketStatus ?? STATE_LABELS[link.ticketState]) : `${link.providerLabel}: ${link.ticketStatus ?? STATE_LABELS[link.ticketState]}`}
      </StatusTag>
      {link.reopenedAt && link.ticketState !== "done" && link.ticketState !== "cancelled" && (
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--c-warningText)" }} title="The ticket was closed and has been reopened. The platform does not reopen anything by itself.">▲ Reopened</span>
      )}
      {link.lastReadError && <span style={{ fontSize: 11, color: "var(--c-warningText)" }} title={link.lastReadError}>▲ Not read</span>}
    </span>
  );
}

/**
 * "Raise ticket", or the ticket once there is one. `canRaise` mirrors the register's own write
 * permission; the server checks it again.
 */
export function TicketControl({ kind, subjectRef, links, canRaise, compact }: {
  kind: TicketSubjectKind;
  subjectRef: string;
  links: TicketLinks;
  canRaise: boolean;
  compact?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!links.available) return null;
  const link = links.linkFor(subjectRef);
  if (link) return <TicketChip link={link} compact={compact} />;
  if (!links.connected || !canRaise) return null;

  const raise = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ link: TicketLinkRow | null }>("/ticketing/links", { kind, ref: subjectRef });
      if (res.link) links.remember(res.link); else links.reload();
    } catch (err) {
      setError(apiErrorMessage(err, "The ticket could not be raised."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <button type="button" onClick={raise} disabled={busy}
        title={`Raise a ticket in ${links.providerLabel ?? "your ticketing system"}`}
        style={{ ...btnSecondary, padding: "3px 10px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, opacity: busy ? 0.6 : 1 }}>
        <Ticket size={12} aria-hidden /> {busy ? "Raising…" : "Raise ticket"}
      </button>
      {error && <span role="alert" style={{ fontSize: 12, color: "var(--c-dangerText)" }}>{error}</span>}
    </span>
  );
}
